import React, { useState, useEffect } from "react";
import { useAuth0 } from "../../auth0/react-auth0-wrapper";
import Layout from "../../components/layout/Layout";
import Container from "../../components/container/Container";
import Loading from "../../components/loading/Loading";
import ISODateString from "../../functions/ISODateString";
import InstrumentGrid from "../../components/instruments/InstrumentGrid";
import InstrumentBooleanText from "../../components/instruments/InstrumentBooleanText";
import InstrumentGauge from "../../components/instruments/InstrumentGauge";
import InstrumentStringValueOnly from "../../components/instruments/InstrumentStringValueOnly";
import UserRoleContext from "../../context/userrolecontext";
import LanguageContext from "../../context/languagecontext";
import GoogleMaps from "../../components/googlemaps/GoogleMaps";
import Styles from "./Machine.module.scss";

const apiUri = process.env.REACT_APP_API_URI;
const apiUpdateSpeed = process.env.REACT_APP_API_UPDATE_SPEED;

const Machine = props => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { getTokenSilently } = useAuth0();
  const userRole = React.useContext(UserRoleContext);
  const { language } = React.useContext(LanguageContext);

  useEffect(() => {
    let repeatFetch = false;
    let runTimeOut = false;
    let runInterVal = false;
    let isCanceled = false;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const token = await getTokenSilently();

        const response = await fetch(
          `${apiUri}/machines/${props.match.params.machinename}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            signal: controller.signal
          }
        );
        if (isCanceled) {
          return;
        }
        if (!response.ok) {
          throw Error(`${response.status}`);
        }
        const data = await response.json();
        if (!isCanceled) {
          setData(data);
          setError(null);
          repeatFetch = true;
        }
      } catch (err) {
        if (!isCanceled) {
          setError(err);
          runTimeOut && clearTimeout(runTimeOut);
          runInterVal && clearInterval(runInterVal);
          return;
        }
      }
    };
    fetchData();

    const getSeconds = new Date().getSeconds();
    const getTimeout =
      getSeconds >= 30 ? (90 - getSeconds) * 1000 : (30 - getSeconds) * 1000;

    runTimeOut = setTimeout(() => {
      fetchData();
      runInterVal = setInterval(() => {
        repeatFetch && fetchData();
      }, apiUpdateSpeed);
    }, getTimeout);

    return () => {
      isCanceled = true;
      controller.abort();
      runTimeOut && clearTimeout(runTimeOut);
      runInterVal && clearInterval(runInterVal);
    };
  }, [getTokenSilently, props.match.params.machinename]);

  const renderContent =
    data !== null && data.length > 0 ? (
      <>
        <div className={Styles.machineInfo}>
          <h1
            className={Styles.machineName}
            title={props.match.params.machinename}
          >
            {props.match.params.machinename}
          </h1>
          <p className={Styles.machineGroup}>
            {`GPS: ${ISODateString(new Date(data[0].ServerRequestTimeStamp))}`}
          </p>
        </div>

        <InstrumentGrid>
          {data.map((data, index) =>
            data.VisualRepresentation === "BooleanText" ? (
              <InstrumentBooleanText key={index} data={data} />
            ) : data.VisualRepresentation === "Gauge" &&
              data.GaugeScaleMin !== null ? (
              <InstrumentGauge key={index} data={data} />
            ) : data.VisualRepresentation === "StringValueOnly" ? (
              <InstrumentStringValueOnly key={index} data={data} />
            ) : null
          )}
        </InstrumentGrid>
      </>
    ) : (
      <Loading />
    );

  return (
    <Layout showBackButton={true} backButtonLink="/">
      <Container growHeight={!data ? true : error ? true : false}>
        {!error ? (
          renderContent
        ) : error.message === "403" ? (
          <Loading
            error={`${
              language === "fr"
                ? "Bienvenue sur vib connect"
                : language === "it"
                ? "Benvenuti a vib connect"
                : "Willkommen bei vib connect"
            }`}
            message={`${
              language === "fr"
                ? "Veuillez vérifier votre boîte de réception et suivre les instructions pour vérifier votre adresse électronique afin d'accéder à vib connect."
                : language === "it"
                ? "Controlla la tua casella di posta elettronica e segui le istruzioni per verificare il tuo indirizzo e-mail per l'accesso a vib connect."
                : "Bitte überprüfen Sie Ihren Posteingang und folgen Sie den Anweisungen, um Ihre E-Mail-Adresse für den Zugang zu vib connect zu verifizieren."
            }`}
          />
        ) : error.message === "423" ? (
          <Loading
            error={`${
              language === "fr"
                ? "Compte en cours…"
                : language === "it"
                ? "Conto in corso…"
                : "Account in Bearbeitung…"
            }`}
            message={`${
              language === "fr"
                ? "Votre accès n'a pas encore été autorisé. Nous prenons la sécurité des données très au sérieux et nous activerons votre compte dès que possible. Vous en serez informé prochainement par vib GmbH par e-mail."
                : language === "it"
                ? "Il vostro accesso non è stato ancora autorizzato. Prendiamo molto sul serio la sicurezza dei dati e attiveremo il vostro account il prima possibile. Sarete informati a breve da vib GmbH via e-mail."
                : "Ihr Zugang wurde noch nicht autorisiert. Wir nehmen die Sicherheit von Daten besonders ernst und werden Ihren Account sobald wie möglich freischalten. Sie werden dazu in Kürze per E-Mail von der vib GmbH benachrichtigt."
            }`}
          />
        ) : error.message === "401" ? (
          <Loading
            error={`${
              language === "fr"
                ? "Pas d'autorisation"
                : language === "it"
                ? "Nessuna autorizzazione"
                : "Keine Autorisierung"
            }`}
            message={`${
              language === "fr"
                ? "Cette machine n'est actuellement pas affectée à votre compte. Veuillez contacter votre administrateur."
                : language === "it"
                ? "Questa macchina non è attualmente assegnata al vostro conto. Contattate il vostro amministratore."
                : "Ihrem Account ist diese Maschine zur Zeit nicht zugewiesen. Bitte wenden Sie sich an Ihren Administrator."
            }`}
          />
        ) : (
          <Loading
            error={`${
              language === "fr"
                ? "Erreur :"
                : language === "it"
                ? "Errore:"
                : "Fehler:"
            } ${error.message}`}
            message={`${
              language === "fr"
                ? "Veuillez réessayer plus tard."
                : language === "it"
                ? "Si prega di riprovare più tardi."
                : "Bitte versuchen Sie es später noch einmal."
            }`}
          />
        )}
      </Container>
      {!error && data !== null && data.length > 0 && (
        <GoogleMaps
          data={[data[0]]}
          cockpit={true}
          language={userRole.language}
        />
      )}
    </Layout>
  );
};

export default Machine;
